/** The OTP sign-in flow — a step machine over static markup, composed from
 * the api/ layer and the shared components. Ported from seekr's
 * accounts/static/email_otp/otp_core.js.
 *
 * One flow, two mount modes:
 *   - the standalone /login page mounts it into a page element;
 *   - the embeddable widget mounts it into a shadow root.
 *
 * The JSX evaluates once to real DOM nodes and the flow keeps direct
 * references to the elements it touches, so nothing queries the host
 * document and a second copy inside a shadow root never collides with it.
 *
 * Flow: entry -> request-code -> 6-digit code -> verify
 *   existing user -> opt-in-only consent step if no live marketing
 *                    subscription -> done()
 *   new user      -> registration step (names + marketing opt-in) ->
 *                    complete-signup (+consent) -> done()
 * Plus social auth (Facebook/Google) and referral passthrough, and — for the
 * visitor whose code email never arrives — the password fallback offered on
 * the code step, which reaches the same consent branch as a verified code.
 *
 * What this module keeps is the choreography — which step follows which, and
 * what each submit does. The mechanics it used to also carry live next door:
 * `stepView.ts` (one step visible, owning the busy state and the error),
 * `resendButton.tsx` (the cooldown button and its ticker),
 * `passwordFallback.tsx` (the delayed "already have a password?" reveal) and
 * `consentCapture.ts` (the consent wording and write it shares with the social
 * registration page).
 */

import {
  completeSignup,
  requestCode,
  signInWithPassword,
  verifyCode,
} from "../api/auth";
import { ApiHttpError } from "../api/errors";
import type { Client } from "../api/client";
import { createConsentService } from "../api/consent";
import { CONSENT_PRIVACY_URL } from "../api/consent.copy";
import type { SeekrUser } from "../api/types";
import { cardHeader, errorBanner } from "../components/card";
import { codeInput } from "../components/codeInput";
import { consentPanel } from "../components/consent";
import { field, gatedButton } from "../components/form";
import { FacebookIcon, GoogleIcon } from "../components/icons";
import { registerForm } from "../components/registerForm";
import { trackWith } from "../lib/analytics";
import { LEGACY, OTP } from "../lib/analyticsEvents";
import { safeFocus } from "../lib/dom";
import { looksLikeEmailAddress } from "../lib/email";
import { h } from "../lib/jsx";
import { submitForm } from "../lib/submitForm";
import {
  hydrateConsentPanels,
  recordConsent,
  reportConsentSaveFailure,
} from "./consentCapture";
import { createPasswordFallback } from "./passwordFallback";
import { createResendButton } from "./resendButton";
import { createStepView, type StepName } from "./stepView";

export interface SignInFlowConfig {
  client: Client;
  wordmarkUrl?: string;
  next?: string;
  referralCode?: string;
  /** Attribution, threaded to analytics and to complete-signup
   * (stored on the seekr user profile). */
  source?: string;
  /** Per-step headings; default to the NO-opt-in copy. */
  headings?: { entry?: string; registration?: string };
  /** Show the marketing-consent step(s). Default true; checkout sets false. */
  showConsent?: boolean;
  /** CTA overrides; default to the design copy. */
  emailCta?: string;
  submitCta?: string;
  createCta?: string;
  /** Optional entry-step subtitle (contextual, per-variant). */
  intro?: string;
  /** The key of the variant above, when it came from one. Carried on the
   * social-auth begin URL so the edge can hand it to the complete-registration
   * page after the provider round trip (src/signinVariant.ts) — the copy
   * bundle itself can't survive that hop. */
  variantKey?: string;
  /** Privacy Policy link target in the consent panel. */
  privacyPolicyUrl?: string;
  onLoggedIn?: (user: SeekrUser | null) => void;
}

export interface SignInFlow {
  reset(): void;
}

export function mountSignInFlow(
  root: HTMLElement,
  cfg: SignInFlowConfig,
): SignInFlow {
  const { client } = cfg;
  const consent = createConsentService(client);
  const next = cfg.next || "/";
  const referral = cfg.referralCode ?? "";
  const source = cfg.source;
  // Every event this flow fires carries its source and variant, so the whole
  // funnel is attributable — added here once rather than at each call.
  const t = trackWith({ source, variant: cfg.variantKey });
  const showConsent = cfg.showConsent !== false;
  const privacyPolicyUrl = cfg.privacyPolicyUrl ?? CONSENT_PRIVACY_URL;
  const headings = {
    entry: cfg.headings?.entry ?? "Sign in or create an account",
    registration: cfg.headings?.registration ?? "Complete registration",
  };

  let email = "";
  let verificationToken = "";
  let pendingUser: SeekrUser | null = null;
  // Guards the code step against a double-submit: a verify is in flight, so
  // neither a second onCodeSubmit nor the CTA gate may act as if the step were
  // idle (all six digits are present, which otherwise reads as "ready").
  let submitting = false;

  // ---- view -----------------------------------------------------------------
  // Handlers are wired inline (function declarations hoist, so the flow
  // below is in scope when the JSX evaluates).

  const header = cardHeader(headings.entry, cfg.wordmarkUrl);
  const error = errorBanner();
  const codeEmailEl = (<strong />) as HTMLElement;

  const emailField = field("otp-email", "Email address", {
    name: "email",
    type: "email",
    autocomplete: "email",
    inputmode: "email",
    placeholder: "Enter your email address",
  });
  const emailSubmitBtn = gatedButton({
    label: cfg.emailCta ?? "Continue with email",
    ready: () => emailField.input.value.trim().length > 0,
    watch: [emailField.input],
  });

  const code = codeInput({
    // codeInput knows when its value changed, whatever changed it — typing,
    // backspace, paste, or clear() on (re-)entering the step.
    onChange: () => codeSubmitBtn.refresh(),
    onComplete: () => submitCode(),
  });
  const codeSubmitBtn = gatedButton({
    label: cfg.submitCta ?? "Verify",
    ready: () => !submitting && code.value().length === 6,
  });

  // The registration step — the same form entries/complete-registration.tsx
  // mounts for a new social user; only the entrypoint and the submit differ.
  const register = registerForm({
    createCta: cfg.createCta,
    showConsent,
    privacyPolicyUrl,
    onSubmit: onRegistrationSubmit,
  });
  const signinConsent = consentPanel({ privacyPolicyUrl });

  const socialQuery = new URLSearchParams();
  if (next) socialQuery.set("next", next);
  if (referral) socialQuery.set("referral_code", referral);
  if (cfg.variantKey) socialQuery.set("variant", cfg.variantKey);

  const resend = createResendButton({ seconds: 30, onResend: resendCode });
  const fallback = createPasswordFallback({
    seconds: 10,
    onSubmit: onPasswordSubmit,
    onOffered: () => t(OTP.PASSWORD_FALLBACK_OFFERED),
  });

  const sections: Record<StepName, HTMLElement> = {
    // The email path leads and the providers sit below the divider, Google
    // first — the order the design puts them in.
    entry: (
      <section>
        {cfg.intro && <p class="otp-intro">{cfg.intro}</p>}
        <form novalidate onSubmit={onEmailSubmit}>
          {emailField.nodes}
          <p class="otp-hint">We'll email you a verification code. No password needed!</p>
          {emailSubmitBtn.el}
        </form>
        <div class="otp-divider"><span>Or</span></div>
        <a class="otp-btn otp-btn-social"
          href={`${client.socialAuthBase}/google-oauth2/?${socialQuery}`}
          onClick={() => t(OTP.SOCIAL_CLICKED, { socialServiceName: "google-oauth2" })}>
          <GoogleIcon />Continue with Google</a>
        <a class="otp-btn otp-btn-social"
          href={`${client.socialAuthBase}/facebook/?${socialQuery}`}
          onClick={() => t(OTP.SOCIAL_CLICKED, { socialServiceName: "facebook" })}>
          <FacebookIcon />Continue with Facebook</a>
      </section>
    ) as HTMLElement,
    code: (
      <section hidden>
        <form novalidate onSubmit={onCodeSubmit}>
          <p>Enter the 6-digit code we emailed to {codeEmailEl}.</p>
          {code.nodes}
          {codeSubmitBtn.el}
        </form>
        {resend.el}
        {fallback.el}
        <button class="otp-btn otp-btn-link" type="button" onClick={() => {
          t(OTP.DIFFERENT_EMAIL_CLICKED);
          showStep("entry");
        }}>
          Use a different email address</button>
      </section>
    ) as HTMLElement,
    registration: (<section hidden>{register.el}</section>) as HTMLElement,
    consent: (
      <section hidden>
        <form novalidate onSubmit={onConsentSubmit}>
          {signinConsent.el}
          <button class="otp-btn otp-btn-primary" type="submit">Continue</button>
        </form>
      </section>
    ) as HTMLElement,
  };

  const view = createStepView({
    sections,
    header,
    error,
    titleFor: (name) =>
      name === "registration" ? headings.registration : headings.entry,
  });

  root.replaceChildren(
    header.el,
    error.el,
    sections.entry,
    sections.code,
    sections.registration,
    sections.consent,
  );

  hydrateConsentPanels(consent, register.consent, signinConsent);

  // ---- step choreography ------------------------------------------------------

  function showStep(name: StepName): void {
    const section = view.show(name);
    if (name === "code") {
      // Clear any stale digits when (re-)entering — e.g. after "Use a
      // different email address" — so the previous session's code can't be
      // submitted against the new email. clear() reports the change, so the CTA
      // re-gates itself to the empty state.
      code.clear();
      resend.start();
      // Unrevealed, and waiting again: arriving here is arriving to use a code,
      // whether it's the first arrival or a second address.
      fallback.reset();
      fallback.arm();
      code.focusFirst();
      return;
    }
    // Any other step means the code step has been left, cooldown or not.
    resend.stop();
    fallback.stop();
    if (name === "entry") emailSubmitBtn.refresh();
    if (name === "registration") {
      register.refreshCta();
      // The registration step carries the consent panel (unless a variant hides
      // it); arriving here is a new user being shown the marketing opt-in.
      if (register.consent) t(OTP.CONSENT_SHOWN, { is_new: true });
    }
    // The standalone consent step is only reached by a returning user whose
    // marketing preference is unset (maybeAskConsent), so is_new is false.
    if (name === "consent") t(OTP.CONSENT_SHOWN, { is_new: false });
    safeFocus(section.querySelector("input"));
  }

  function done(user: SeekrUser | null): void {
    if (cfg.onLoggedIn) {
      cfg.onLoggedIn(user);
    } else {
      window.location.assign(next);
    }
  }

  // ---- steps ------------------------------------------------------------------

  function onEmailSubmit(e: Event): void {
    e.preventDefault();
    const addr = emailField.input.value.trim();
    if (!addr) return;
    // Caught here rather than by the browser: the form is novalidate (its
    // built-in bubble doesn't match the card's error styling), and seekr's
    // rejection of a non-address costs a round trip to say the same thing.
    if (!looksLikeEmailAddress(addr)) {
      t(OTP.EMAIL_INVALID);
      view.reject("Enter a valid email address.");
      safeFocus(emailField.input);
      return;
    }
    view.busy(e.currentTarget as HTMLFormElement);
    void (async () => {
      try {
        email = addr;
        await requestCode(client, addr);
        codeEmailEl.textContent = addr;
        register.setEmail(addr);
        t(OTP.EMAIL_SUBMITTED);
        showStep("code");
      } catch (err) {
        t(OTP.EMAIL_SUBMIT_FAILED);
        view.fail(err, "Couldn't send a code. Please try again.");
      }
    })();
  }

  function resendCode(): void {
    void (async () => {
      try {
        await requestCode(client, email);
        resend.start();
        t(OTP.EMAIL_SUBMITTED, { resend: true });
      } catch (err) {
        t(OTP.EMAIL_SUBMIT_FAILED, { resend: true });
        view.fail(err, "Couldn't resend a code. Please try again.");
      }
    })();
  }

  function submitCode(): void {
    // Programmatic submit from codeInput's onComplete (edge-triggered on the
    // 6th digit) — routes through the same onCodeSubmit as a click/Enter.
    const form = sections.code.querySelector("form");
    if (form) submitForm(form);
  }

  function onCodeSubmit(e: Event): void {
    e.preventDefault();
    if (submitting) return;
    const value = code.value().trim();
    if (value.length !== 6) return;
    submitting = true;
    view.busy(e.currentTarget as HTMLFormElement);
    void (async () => {
      try {
        const data = await verifyCode(client, email, value);
        t(OTP.CODE_VERIFIED, { is_new: !!data.is_new });
        if (data.is_new) {
          verificationToken = data.verification_token ?? "";
          showStep("registration");
        } else {
          // An existing user is now signed in — the returning-user counterpart
          // to Sign up success. Awaited: maybeAskConsent can navigate straight
          // away, and an unflushed event dies with the page.
          await t(OTP.LOGIN_SUCCESS, { method: "email" });
          await maybeAskConsent(data);
        }
        // Success paths navigate away / advance to another step, so the guard
        // stays set — we never want to re-verify a code that already worked.
      } catch (err) {
        submitting = false;
        t(OTP.CODE_VERIFY_FAILED);
        view.fail(err, "That code is invalid or has expired.");
      }
    })();
  }

  /** The fallback for a code that never arrived: the same account, signed in
   * with the password it already had. Deliberately routed through
   * maybeAskConsent rather than done(), so an existing user with no live
   * marketing subscription is still asked — the consent step belongs to signing
   * in, not to the emailed code.
   *
   * A null user is seekr reporting a session already exists (see
   * signInWithPassword); there's nothing to ask consent about on a session this
   * flow didn't create, so it completes. */
  function onPasswordSubmit(e: Event): void {
    e.preventDefault();
    const password = fallback.value();
    if (!password) return;
    view.busy(e.currentTarget as HTMLFormElement);
    void (async () => {
      try {
        const user = await signInWithPassword(client, email, password);
        t(OTP.PASSWORD_SIGNIN);
        if (user) {
          await t(OTP.LOGIN_SUCCESS, { method: "password" });
          await maybeAskConsent(user);
        } else {
          done(null);
        }
      } catch (err) {
        t(OTP.PASSWORD_SIGNIN_FAILED);
        // Stays on the code step: a code that turns up late still works.
        view.fail(err, "Couldn't sign you in. Please try again.");
      }
    })();
  }

  /** The user is already logged in at this point; the consent step only
   * gates the redirect, never the sign-in itself. */
  async function maybeAskConsent(user: SeekrUser): Promise<void> {
    if (!showConsent) {
      done(user);
      return;
    }
    try {
      if (await consent.isSubscribedToMarketing(email)) {
        done(user);
      } else {
        pendingUser = user;
        showStep("consent");
      }
    } catch {
      done(user);
    }
  }

  function onRegistrationSubmit(e: Event): void {
    e.preventDefault();
    const { firstName, lastName } = register.values();
    if (!firstName || !lastName) return;
    view.busy(e.currentTarget as HTMLFormElement);
    void (async () => {
      try {
        const user = await completeSignup(client, {
          verificationToken,
          firstName,
          lastName,
          referralCode: referral,
          source,
        });
        await t(LEGACY.SIGN_UP_SUCCESS, { method: "email" });
        // The account exists and the user is signed in, so consent is recorded
        // beside that, never in front of it: recordConsent reports its own
        // failures and tells us only what actually landed.
        const recorded = await recordConsent(
          consent,
          register.consent,
          "on signup form",
          t,
        );
        if (recorded) {
          // Awaited: done() navigates immediately after this, and this is the
          // last event of the signup funnel — exactly the one a page load eats.
          await t(OTP.CONSENT_SET, {
            opted_in: recorded.optedIn,
            is_new: true,
          });
        }
        done(user);
      } catch (err) {
        // Legacy tracked only the server-500 signup failure; keep that string
        // verbatim and cover the rest under the new vocabulary.
        if (err instanceof ApiHttpError && err.status >= 500) {
          t(LEGACY.SIGN_UP_FORM_500_ERROR);
        } else {
          t(OTP.SIGNUP_FAILED, {
            status: err instanceof ApiHttpError ? err.status : undefined,
          });
        }
        view.fail(err, "signup failed");
      }
    })();
  }

  function onConsentSubmit(e: Event): void {
    e.preventDefault();
    view.busy(e.currentTarget as HTMLFormElement);
    const user = pendingUser;
    void (async () => {
      try {
        // Not recordConsent: this visitor is sitting on a screen whose only
        // purpose is the answer, so a failed write is surfaced and retried
        // rather than reported past them. Reported to us either way, below.
        await consent.save(signinConsent.box.checked, "when signing in");
        await t(OTP.CONSENT_SET, {
          opted_in: signinConsent.box.checked,
          is_new: false,
        });
        done(user);
      } catch (err) {
        // The visitor first: showing the retry must not wait on an analytics
        // ack. Then report — a visitor stuck retrying this screen needs to be
        // as visible as the silent drops, and nothing here navigates away to
        // cut the event short.
        view.fail(err, "Couldn't save your preference. Please try again.");
        void reportConsentSaveFailure(err, "when signing in", t);
      }
    })();
  }

  return {
    reset(): void {
      email = "";
      verificationToken = "";
      pendingUser = null;
      submitting = false;
      code.clear();
      // Don't leave a typed password sitting in the DOM of a closed modal.
      fallback.reset();
      showStep("entry");
    },
  };
}
